<template>
  <VariantsList
    v-model:open="openVariantList"
    :variants="variants"
    @selectedVariant="handleSelectedVariant"
  />
  <AddedToBag
    v-model:open="openAddedToBag"
    :variant="selectedVariant"
    @editVariant="handleEditVariant"
  />
  <BagFailure
    v-model:open="openBagFailure"
    message="We're trying to make our sales as fair as possible so we've limited each order to a maximum of two sizes per style and a maximum of six pieces in total"
  />
  <StockNotification
    v-model:open="openStockNotification"
    :variant="selectedVariant"
  />
</template>

<script>
import { unwrapNodes } from "@/helpers/graphql";
import { mapActions, mapState } from "vuex";
import isEmpty from "lodash-es/isEmpty";
import AddedToBag from "@/components/add-to-bag/AddedToBag.vue";
import StockNotification from "@/components/StockNotification.vue";
import * as Sentry from "@sentry/vue";
import VariantsList from "@/components/add-to-bag/VariantsList.vue";

import { defineAsyncComponent } from "vue";
const BagFailure = defineAsyncComponent(() =>
  import(
    /* webpackChunkName: "BagFailure" */ "@/components/add-to-bag/BagFailure.vue"
  )
);

export default {
  name: "AddToBag",
  components: {
    VariantsList,
    AddedToBag,
    StockNotification,
    BagFailure,
  },
  data() {
    return {
      openVariantList: false,
      openAddedToBag: false,
      openStockNotification: false,
      openBagFailure: false,
      variantId: null,
      originalVariant: {},
    };
  },
  computed: {
    ...mapState("add-to-bag", ["addToBagOpen", "product"]),
    ...mapState("cart", ["cart"]),
    openProperties() {
      const {
        openVariantList,
        openAddedToBag,
        openStockNotification,
        openBagFailure,
      } = this;
      return {
        openVariantList,
        openAddedToBag,
        openStockNotification,
        openBagFailure,
      };
    },
    selectedVariant() {
      if (!this.variantId && this.variants.length === 1) {
        return this.variants[0];
      }

      return (
        this.variants.find((variant) => variant.id === this.variantId) || {}
      );
    },
    variants() {
      const variants = unwrapNodes(this.product?.variants?.edges) || [];

      const sizeOnlyVariants = variants.map((variant) => {
        const title = variant.title.split(" / ")[0];

        return {
          ...variant,
          title,
        };
      });

      return sizeOnlyVariants || null;
    },
  },
  watch: {
    openProperties: {
      handler: function (val) {
        const areAllClosed = Object.values(val).every(
          (value) => value === false
        );

        if (areAllClosed) {
          this.closeAddToBag();
        }
      },
      deep: true,
    },
    async addToBagOpen(newVal, oldVal) {
      if (oldVal === false && newVal === true) {
        this.openVariantList = false;
        this.openAddedToBag = false;
        this.openStockNotification = false;
        this.openBagFailure = false;

        const hasMultipleVariants = this.variants.length > 1;
        const isAvailableForSale = this.product.availableForSale;

        if (!hasMultipleVariants && !isAvailableForSale) {
          this.openStockNotification = true;
        } else if (hasMultipleVariants) {
          this.openVariantList = true;
        } else {
          const variantId = this.variants[0].id;
          this.variantId = variantId;

          await this.addToBag(variantId);
        }
      }
    },
  },
  methods: {
    ...mapActions("add-to-bag", ["closeAddToBag"]),
    ...mapActions("cart", [
      "setCartData",
      "addCartItem",
      "removeCartItem",
      "checkCartRules",
    ]),
    async handleSelectedVariant({ id, quantityAvailable }) {
      this.variantId = id;

      if (quantityAvailable === 0) {
        this.handleNotifyMe(id);
        return;
      }

      await this.addToBag(id);
    },
    async addToBag(id) {
      let quantity = 1;

      if (!isEmpty(this.originalVariant)) {
        const lines = unwrapNodes(this.cart.lines.edges);

        const cartLine = lines.find((line) => {
          return line.merchandise.id === this.originalVariant.id;
        });

        await this.removeCartItem(cartLine.id);
        quantity = this.originalVariant.quantity;
      }

      const cartRuleResults = await this.checkCartRules(this.product.id);
      if (!cartRuleResults) {
        this.openBagFailure = true;
        return;
      } else {
        this.openAddedToBag = true;
      }

      this.openVariantList = false;

      try {
        await this.addCartItem({ id, quantity });
        this.originalVariant = {};
      } catch (error) {
        Sentry.captureException(error);
        console.log(error);
      }
    },
    handleAddedToBag(variantId) {
      this.variantId = variantId;
      this.originalVariant = {};
      this.openAddToBag = false;
      this.openAddedToBag = true;
      this.openStockNotification = false;
    },
    handleNotifyMe(variantId) {
      this.variantId = variantId;
      this.openVariantList = false;
      this.openNotifyMe = false;
      this.openStockNotification = true;
    },
    handleEditVariant(originalVariant) {
      this.openVariantList = true;
      this.openAddedToBag = false;
      this.originalVariant = originalVariant;
    },
  },
};
</script>
